import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { action, computed, observable } from "mobx";
import { HotelConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers, Room } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import {
  TypeaheadSelectionProps,
  TypeaheadSearchDetail,
  TypeaheadDataProps,
} from "src/components/flexComponents/WizardHotelPWA/typings";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Logger } from "bernie-logger";
import { travelersStateOnConfig } from "../global/travelers/travelersState";
import { TravelersState } from "stores/wizard/state/global";
import { HotelRatesType } from "stores/wizard/state/typings";
import { TravelAgentSearchWizardConfigQuery } from "__generated_deprecated__/api";
import { getCurrentLocationSelection } from "components/utility/GeolocationUtil";
import TravelAgentSearchWizardConfig = TravelAgentSearchWizardConfigQuery.TravelAgentSearchWizardConfig;
import { ValidationData, ValidationState } from "../validations/typings";
import { WizardValidationState } from "../validations/WizardValidationState";
import { LastSearchParams } from "src/components/flexComponents/WizardHotelPWAv2/utils/LastSearchTypes";

export interface ToggleCheckbox {
  stateToForce?: boolean;
}

export class HotelWizardState implements LOBState {
  logger: Logger;

  private globalState: GlobalWizardState;

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: HotelConfig;

  @observable
  public subLOBState = "";

  public validations: ValidationState;

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public numberOfErrors = 0;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public isGeolocationAllowed = true;

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  @observable
  public hasCalledLastSearch = false;

  @action
  public updateHasLastSearch(state: boolean) {
    this.hasCalledLastSearch = state;
  }

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @computed
  public get date() {
    return this.globalState.date;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @observable
  public hotelTravelers: TravelersState;

  @computed
  public get travelers() {
    return this.hotelTravelers;
  }

  @observable
  public hotelTabSelected = true;

  @action
  public toggleTabSelected = () => {
    this.hotelTabSelected = !this.hotelTabSelected;
  };

  @observable
  public isMultiplePlacesChecked = false;

  @action
  public toggleMultiplePlacesCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isMultiplePlacesChecked = stateToForce ?? !this.isMultiplePlacesChecked;
  };

  @observable
  public isAddAFlightChecked = false;

  @action
  public toggleAddAFlightCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isAddAFlightChecked = stateToForce ?? !this.isAddAFlightChecked;
  };

  @observable
  public isAddACarChecked = false;

  @action
  public toggleAddACarCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isAddACarChecked = stateToForce ?? !this.isAddACarChecked;
  };

  @observable
  public hotelTypeSubTab: HotelRatesType = HotelRatesType.STANDALONE;

  @action
  public updateHotelTypeSubTab(hotelTypeSubTab: HotelRatesType) {
    this.cleanErrorState();
    this.hotelTypeSubTab = hotelTypeSubTab;
  }

  @observable
  public isPackageRatesTermsChecked = false;

  @action
  public togglePackageRatesTermsCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isPackageRatesTermsChecked = stateToForce ?? !this.isPackageRatesTermsChecked;
  };

  @observable
  public isNewExperienceTermsChecked = false;

  @action
  public toggleNewExperienceTermsCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isNewExperienceTermsChecked = stateToForce ?? !this.isNewExperienceTermsChecked;
  };

  @computed
  public get hotelPackageType() {
    if (this.isAddAFlightChecked && this.isAddACarChecked) {
      return "fhc";
    }
    if (this.isAddAFlightChecked) {
      return "fh";
    }
    if (this.isAddACarChecked) {
      return "hc";
    }

    return "";
  }

  @observable
  public regionId: string;

  @action
  public setRegionId(regionId: string) {
    this.regionId = regionId;
  }

  @computed
  public get hotelRegionId() {
    return this.regionId;
  }

  @observable
  public htgStartDate = "";

  @computed
  public get startDate() {
    return this.htgStartDate;
  }

  @action
  public setHtgStartDate(htgStartDate: string) {
    this.htgStartDate = htgStartDate;
  }

  @observable
  public htgEndDate = "";

  @computed
  public get endDate() {
    return this.htgEndDate;
  }

  @action
  public setHtgEndDate(htgEndDate: string) {
    this.htgEndDate = htgEndDate;
  }

  @computed
  public get formAction() {
    if (this.hotelTypeSubTab === HotelRatesType.NEW_PROPERTY_SEARCH) {
      return this.config.form.actionPropertySearch;
    }

    if (this.hotelTypeSubTab !== HotelRatesType.PACKAGE_RATES && (this.isAddAFlightChecked || this.isAddACarChecked)) {
      return this.config.form.actionPackage;
    }

    return this.config.form.action;
  }

  // validations
  @observable
  public destinationInvalidKey: string;

  @observable
  public originInvalidKey: string;

  @observable
  public hotelTravelersInvalidKey = "";

  @observable
  public travelAgentPackageRatesDetail: TravelAgentSearchWizardConfigQuery.PackageRatesDetail;

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action
  public validateLessThanNTravelers = () => {
    const isValid = this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers);
    if (!isValid) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateChildInfantFields = () => {
    /* istanbul ignore if */
    if (this.config.travelers.withRooms && !this.globalState.validateChildInfantFields(this.travelers)) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidChildInfantValuesMessageToken!;
      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers() && this.validateChildInfantFields();

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @observable
  public advancedOptionsFilterVisible = true;

  @observable
  public checkboxFilterChecks = {
    businessFriendly: false,
    familyFriendly: false,
    freeCancellation: false,
    breakfastIncluded: false,
  };

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
  };

  // other actions
  @action
  public overrideConfig(callback: () => void) {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @observable
  public sourceIdSelected = "";

  @action
  public formatTravelersFromHistoryDetail = (detail: TypeaheadSearchDetail): Room[] => {
    const adults = detail.adultTravelerCounts || [];
    const childrenAges = detail.childrenAges || [];
    const rooms = adults.map((adultsInRoom) => {
      const childrenInRoom = childrenAges.flatMap((children) => children.map((child) => ({ age: child })));
      return { adults: adultsInRoom, children: childrenInRoom, infants: [] };
    });
    return rooms;
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    const { historyDetail } = this.config.location.destination.essAdapterConfig;
    const { data } = (selection as TypeaheadSelectionProps) || {};
    const { selected } = data || "";
    this.sourceIdSelected = selected;
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";

    if (historyDetail) {
      this.prefillFormWithHistoryDetail(data);
    }
  };

  @action
  public getDateTimezoneOffsetFromDatestamp = (date: number): Date => {
    const dateFormatted = new Date(date);
    const dateOffset = dateFormatted.getTimezoneOffset() * 60000;
    return new Date(dateFormatted.getTime() + dateOffset);
  };

  @action
  public prefillFormWithHistoryDetail = (data: TypeaheadDataProps) => {
    if (data.searchDetail) {
      const { searchDetail } = data;
      if (searchDetail.endDate && searchDetail.startDate) {
        const { startDate, endDate } = searchDetail;
        this.updateDateSelection(
          this.getDateTimezoneOffsetFromDatestamp(startDate),
          this.getDateTimezoneOffsetFromDatestamp(endDate)
        );
      }

      if (
        (searchDetail.adultTravelerCounts || searchDetail.childrenAges) &&
        searchDetail.roomCount === 1 &&
        searchDetail.lob === "Lodging"
      ) {
        this.updateHotelTravelersSelection({
          rooms: this.formatTravelersFromHistoryDetail(searchDetail),
          infantSeating: this.travelers.hotel.infantSeating,
        });
      }
    }
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    this.originInvalidKey = "";
  };

  @action
  public changeFormFilterProperties = (event: React.ChangeEvent<HTMLFormElement>) => {
    const { key: filterKey, checked: isChecked, name: filterName, value: filterValue } = event.currentTarget;
    this.checkboxFilterChecks[filterKey] = isChecked;

    const trackingInfo: FlexTrackingInfo = {
      moduleName: "HOT.SF",
      rfrr: `${filterName}.${filterValue}.${isChecked}`,
      action: Action.CLICK,
      linkName: `${filterName}.${filterValue}`,
    };

    sendDelayedTrackEvent(trackingInfo, this.config.analytics);
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers, this.travelers);
    this.hotelTravelersInvalidKey = "";
  };

  public updateDestinationFromContext(context: ExtendedContextStore) {
    const location = context?.searchContext?.location || context?.searchContext?.destination;

    if (location?.extendedName) {
      this.globalState.updateDestinationFromContext(
        location.extendedName,
        location.id || "",
        "",
        location.geoCoordinate ? `${location.geoCoordinate.latitude},${location.geoCoordinate.longitude}` : "",
        location.code || "",
        "",
        ""
      );
      this.destinationInvalidKey = "";
    }
  }

  public updateStateValidations = (validationsData: any) => {
    this.updateLocErrorKeys(validationsData.invalidKeys);
    this.errorInputRef = validationsData.inputReference;
    this.numberOfErrors = validationsData.numberOfErrors;
  };

  public updateLocErrorKeys = (invalidKeys: any) => {
    this.dateEndInvalidKey = invalidKeys.dateEnd;
    this.destinationInvalidKey = invalidKeys.destination;
    this.hotelTravelersInvalidKey = invalidKeys.travelers;
    this.originInvalidKey = invalidKeys.origin;
  };

  private submitWithUserLocation = (event: React.FormEvent) => {
    event.preventDefault();

    getCurrentLocationSelection()
      .then((selection) => {
        this.updateDestinationSelection(selection);
        (document.getElementById(this.config.elementId!) as HTMLFormElement)?.submit();
      })
      .catch(() => {
        this.isGeolocationAllowed = false;
        this.postSubmit();
      });
  };

  private postSubmit = (event?: React.FormEvent) => {
    const { origin, destination } = this.location;
    const { start, end } = this.date;

    const dataToValidate: ValidationData = {
      location: {
        origin: this.isAddAFlightChecked ? origin.value : null,
        destination: destination.value,
      },
      dates: {
        start,
        end,
      },
      references: this.wizardInputsArray,
    };

    const { isValid, data } = this.validations.validateForm(dataToValidate, this.config);

    if (!isValid) {
      this.updateStateValidations(data);
      event?.preventDefault();
      this.validations.focusErrorSummary();
    }
  };

  public submit = (event: React.FormEvent) => {
    if (this.config.location.destination.defaultToUserLocation && !this.location.destination.value) {
      return this.submitWithUserLocation(event);
    }

    this.postSubmit(event);
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  public getLastSearchFromAPI(params: LastSearchParams) {
    const lastSearchParams = new URLSearchParams({
      client: "Homepage",
      dest: "true",
      expuserid: params.expuserid?.toString() || "-1",
      features: "ta_hierarchy|postal_code|google|consistent_display",
      format: "json",
      guid: params.guid,
      lob: "HOTELS",
      locale: params.locale,
      maxresults: "1",
      personalize: "true",
      regiontype: params.regiontype || "2047",
      siteid: params.siteid.toString(),
      latlong: "",
      ab: "39800.1",
    }).toString();

    return Promise.resolve(
      fetch(`/api/v4/typeahead/?${lastSearchParams}`, {
        method: "GET",
      })
    );
  }

  @action
  public updateFromTravelAgentConfig(travelAgentConfig: TravelAgentSearchWizardConfig) {
    if (travelAgentConfig) {
      this.config.enablePackageRates = travelAgentConfig.packageRatesEnabled;
      if (travelAgentConfig.enabledLobs && travelAgentConfig.enabledLobs.length === 1) {
        this.config.heading.hideLobTitle = false;
      }
      if (travelAgentConfig.packageRatesDetail) {
        this.travelAgentPackageRatesDetail = travelAgentConfig.packageRatesDetail;
      }
    }
  }

  constructor(config: HotelConfig, globalState: GlobalWizardState, logger: Logger) {
    this.config = config;
    this.globalState = globalState;
    this.logger = logger;
    this.hotelTravelers = travelersStateOnConfig(this.config.travelers);
    this.validations = new WizardValidationState();
  }
}
